import useThemeColors from '@/hooks/useThemeColors';
import clsx from 'clsx';
import React, { forwardRef } from 'react';
import InputError from './InputError';

interface TextInputProps {
    label?: string;
    id?: string;
    name: string;
    value: string | number;
    onChange: (value: string) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    type?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    autoFocus?: boolean;
    className?: string;
    error?: string | string[] | null;
    helpText?: React.ReactNode;
    maxLength?: number;
    min?: number;
    max?: number;
    step?: number;
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
    (
        {
            label,
            id,
            name,
            value,
            onChange,
            onBlur,
            type = 'text',
            placeholder,
            required = false,
            disabled = false,
            readOnly = false,
            autoFocus = false,
            className = '',
            error,
            helpText,
            maxLength,
            min,
            max,
            step,
        },
        ref
    ) => {
        const colors = useThemeColors();

        return (
            <div className={`${className}`}>
                {label && (
                    <label
                        htmlFor={id || name}
                        className="mb-1 block text-sm font-medium text-gray-500 dark:text-neutral-400"
                    >
                        {label}
                        {required && <span className="ml-1 text-red-500">*</span>}
                    </label>
                )}

                <input
                    ref={ref}
                    id={id || name}
                    name={name}
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly={readOnly}
                    autoFocus={autoFocus}
                    maxLength={maxLength}
                    min={min}
                    max={max}
                    step={step}
                    className={clsx(
                        'w-full rounded-md border px-3 py-2 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-neutral-500',
                        error
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                            : `border-gray-300 dark:border-neutral-600 ${colors.inputFocus}`,
                        disabled && 'cursor-not-allowed bg-gray-50 dark:bg-neutral-900 text-gray-500 dark:text-neutral-400',
                        readOnly && 'bg-gray-50 dark:bg-neutral-900'
                    )}
                />

                {helpText && !error && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">{helpText}</p>
                )}

                <InputError error={error} />
            </div>
        );
    }
);

TextInput.displayName = 'TextInput';

export default TextInput;